import { useEffect, useRef, useState } from "react";
import {
  languagesDataType,
  SpecsDataType,
} from "../../interfaces/admin.interface";
import { Helmet } from "react-helmet";
import { message, Modal, Popconfirm, Switch, Table, TableProps } from "antd";
import * as Yup from "yup";
import { useFormik } from "formik";
import { LanguageService } from "../../services/languages.service";
import ErrorMessage from "../../components/shared/ErrorMessage/ErrorMessage";
import MainButton from "../../components/shared/MainButton/MainButton";
import Spinner from "../../components/shared/Spinner/Spinner";
import { CategoriesSpecsService } from "../../services/category_specs.service";
import SelectCategory from "../../components/admin-pages/products/select-category/SelectCategory";
import ChangeLanguage from "../../components/shared/change-language/ChangeLanguage";
import SelectGroupName from "../../components/category-specs/select-group-names/SelectGroupNames";
import { FaPlus } from "react-icons/fa";

const CategorySpecs = () => {
  const [specs, setSpecs] = useState<SpecsDataType[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editingRecord, setEditingRecord] = useState<SpecsDataType | null>(
    null
  );
  const [langs, setLangs] = useState<languagesDataType[]>([]);
  const [langCode, setLangCode] = useState("AZ");
  const [data, setData] = useState([
    { id: "", text: "", translations: [{ lang_code: "AZ", text: "" }] },
  ]);
  const [selectedName, setSelectedName] = useState<string | undefined>(
    undefined
  );
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(
    null
  );
  const initialSpecsValues = useRef({
    name: "",
    group_name_id: 0 as number | null,
    category_id: 0,
    show_in_filter: false,
    price_mode: false,
    options: [] as {
      id: string;
      text: string;
      translations: {
        lang_code: string;
        text: string;
      }[];
    }[],
    translations: [] as { lang_code: string; name: string }[],
  });

  useEffect(() => {
    setLoading(true);
    CategoriesSpecsService.getAllCategoriesSpecs()
      .then((response) => {
        if (Array.isArray(response?.data?.data)) {
          const dataWithKeys = response?.data?.data?.map(
            (item: SpecsDataType, index: number) => ({
              ...item,
              key: index + 1,
            })
          );
          setSpecs(dataWithKeys);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);
  useEffect(() => {
    LanguageService.getAllLanguages().then((response) =>
      setLangs(response?.data)
    );
  }, []);
  const handleAfterSubmit = async () => {
    setLoading(true);
    setIsModalVisible(false);
    resetForm();
    const updatedData = await CategoriesSpecsService.getAllCategoriesSpecs();
    setSpecs(
      updatedData?.data?.data?.map((item: SpecsDataType, index: number) => ({
        ...item,
        key: index + 1,
      }))
    );
  };
  const { handleSubmit, values, resetForm, setFieldValue, touched, errors } =
    useFormik({
      initialValues: {
        name: "",
        category_id: 0,
        group_name_id: null,
        show_in_filter: false,
        price_mode: false,
        options: [] as {
          id: string;
          text: string;
          translations: {
            lang_code: string;
            text: string;
          }[];
        }[],
        translations: [] as { lang_code: string; name: string }[],
      },
      validationSchema: Yup.object({
        name: Yup.string().required("Xüsusiyyətin adı tələb olunur"),
        category_id: Yup.number()
          .required("Kateqoriya tələb olunur")
          .moreThan(0, "Kateqoriya seçilməlidir"),
      }),
      onSubmit: async (values) => {
        const azTranslation = {
          lang_code: "AZ",
          name: values.name,
        };
        const updatedTranslations = [
          azTranslation,
          ...values.translations.filter((t) => t.lang_code !== "AZ"),
        ];
        const finalValues = {
          ...values,
          translations: updatedTranslations,
          options: values.options
            .filter((option) => option.text.trim() !== "")
            .map((option) => ({
              ...option,
              translations: option.translations.filter(
                (t) => t.text.trim() !== ""
              ),
            })),
        };
        if (isEditing && editingRecord) {
          const isEqual =
            finalValues.name === initialSpecsValues.current.name &&
            finalValues.category_id ===
              initialSpecsValues.current.category_id &&
            finalValues.show_in_filter ===
              initialSpecsValues.current.show_in_filter &&
            finalValues.price_mode === initialSpecsValues.current.price_mode &&
            finalValues.group_name_id ===
              initialSpecsValues.current.group_name_id &&
            JSON.stringify(finalValues.options) ===
              JSON.stringify(initialSpecsValues.current.options) &&
            JSON.stringify(finalValues.translations) ===
              JSON.stringify(initialSpecsValues.current.translations);
          if (isEqual) {
            message.info("Dəyişiklik edilmədi.");
          } else {
            const response = await CategoriesSpecsService.updateCategorySpec({
              ...finalValues,
              id: editingRecord.id,
            });
            if (response.status === 200) {
              message.success("Xüsusiyyət uğurla yeniləndi!");
              await handleAfterSubmit();
            } else {
              message.error(
                response.errorMessage || "Xüsusiyyəti yeniləmək alınmadı."
              );
            }
          }
        } else {
          const response = await CategoriesSpecsService.addCategorySpec(
            finalValues
          );
          if (response.status === 200) {
            message.success("Yeni Xüsusiyyət uğurla əlavə edildi!");
            await handleAfterSubmit();
            setFieldValue("group_name_id", null);
          } else {
            message.error(
              response.errorMessage || "Xüsusiyyət əlavə etmək alınmadı."
            );
          }
        }
        setLoading(false);
      },
    });
  const handleAddOption = () => {
    const newOption = {
      text: "",
      translations: langs.map((lang) => ({ lang_code: lang.code, text: "" })),
    };
    setData((prevData) => [...prevData, { ...newOption, id: "" }]);
    setFieldValue("options", [...values.options, newOption]);
    setSelectedName(undefined);
  };
  const handleOptionChange = (
    index: number,
    value: string,
    langCode: string
  ) => {
    const updatedData = data.map((item, i) =>
      i === index
        ? {
            ...item,
            ...(langCode === "AZ"
              ? {
                  text: value,
                  translations: [
                    ...item.translations.filter(
                      (translation) => translation.lang_code !== "AZ"
                    ),
                    { lang_code: "AZ", text: value },
                  ],
                }
              : {
                  translations: item.translations.map((translation) =>
                    translation.lang_code === langCode
                      ? { ...translation, text: value }
                      : translation
                  ),
                }),
          }
        : item
    );

    setData(updatedData);
    setFieldValue("options", updatedData);
  };

  const handleRemoveOption = (index: number) => {
    const updatedData = data.filter((_, i) => i !== index);
    setData(updatedData);
    setFieldValue("options", updatedData);
  };
  const handleTranslationChange = (value: string) => {
    if (langCode === "AZ") {
      setFieldValue("name", value);
    } else {
      const updatedTranslations = [...values.translations];
      const existingTranslationIndex = updatedTranslations.findIndex(
        (t) => t.lang_code === langCode
      );

      if (existingTranslationIndex !== -1) {
        updatedTranslations[existingTranslationIndex] = {
          ...updatedTranslations[existingTranslationIndex],
          name: value,
        };
      } else {
        updatedTranslations.push({ lang_code: langCode, name: value });
      }
      setFieldValue("translations", updatedTranslations);
    }
  };
  const handleDeleteClick = async (record: SpecsDataType) => {
    try {
      await CategoriesSpecsService.deleteCategorySpec(record.id.toString());
      setSpecs((prevData) => prevData.filter((item) => item.id !== record.id));
      message.success("Xüsusiyyət uğurla silindi!");
    } catch (error) {
      message.error("Xüsusiyyəti silmək alınmadı.");
    }
  };
  const handleSwitchChange = async (checked: boolean, recordId: number) => {
    CategoriesSpecsService.updateCategorySpec({
      id: recordId,
      show_in_filter: checked,
    });
    if (checked) {
      message.success("Xüsusiyyət artıq filterdə görünür.");
    } else {
      message.info("Xüsusiyyət artıq filterdə görünmür.");
    }
    setSpecs((prevData) =>
      prevData.map((item) =>
        item.id === recordId ? { ...item, show_in_filter: checked } : item
      )
    );
  };
  const columns: TableProps<SpecsDataType>["columns"] = [
    {
      title: "№",
      dataIndex: "key",
      sorter: (a, b) => a.key - b.key,
    },
    {
      title: "Xüsusiyyət adı",
      dataIndex: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: "Qrup adı",
      dataIndex: "group_name",
      render: (_, record) => <p>{record.group_name?.name}</p>,
    },
    {
      title: "Seçimlər",
      dataIndex: "options",
      render: (_, record) => (
        <div>
          {record.options.map((element: any) => (
            <p key={element.id}>{element.text}</p>
          ))}
        </div>
      ),
    },
    {
      title: "Kateqoriya",
      dataIndex: "category_id",
      render: (_, record) => {
        const category = specs?.find(
          (spec) => spec.category_id === record.category_id
        );
        return category ? category.category?.name : "Naməlum Kateqoriya";
      },
    },
    {
      title: "Qiymətə təsir göstərməsi",
      dataIndex: "price_mode",
      render: (_, record) => (
        <div className="switch_input">
          <Switch disabled checked={record.price_mode} />
        </div>
      ),
    },
    {
      title: "Filtrdə göstərin",
      dataIndex: "show_in_filter",
      render: (_, record) => (
        <div className="switch_input">
          <Switch
            checked={record.show_in_filter}
            onChange={(checked) => handleSwitchChange(checked, record.id)}
          />
        </div>
      ),
    },
    {
      title: (
        <div className="settings">
          <i className="fa-solid fa-gear"></i>
        </div>
      ),
      key: "action",
      render: (_, record) => (
        <div className="settings_icons">
          <div className="edit" onClick={() => handleEditClick(record)}>
            <i className="fa-solid fa-pen-to-square"></i>
          </div>
          <Popconfirm
            title="Bu xüsusiyyəti siləcəyinizə əminsiniz?"
            onConfirm={() => handleDeleteClick(record)}
            onCancel={() => message.info("Silinmə əməliyyatı ləğv edildi.")}
            okText="Bəli"
            cancelText="Xeyr"
            placement="topRight"
          >
            <div className="delete">
              <i className="fa-solid fa-trash"></i>
            </div>
          </Popconfirm>
        </div>
      ),
    },
  ];

  const handleAddClick = () => {
    setIsEditing(false);
    setEditingRecord(null);
    setIsModalVisible(true);
    resetForm();
    setLangCode("AZ");
    setData([
      {
        id: "",
        text: "",
        translations: langs.map((lang) => ({ lang_code: lang.code, text: "" })),
      },
    ]);
    setSelectedName(undefined);
    setSelectedCategoryId(null);
  };

  const handleEditClick = (record: SpecsDataType) => {
    setIsEditing(true);
    setEditingRecord(record);
    setIsModalVisible(true);
    setFieldValue("name", record.name);
    setFieldValue("group_name_id", record.group_name_id);
    setFieldValue("show_in_filter", record.show_in_filter);
    setFieldValue("price_mode", record.price_mode);
    setFieldValue("category_id", record.category_id);
    setFieldValue("translations", record.translations || []);
    setLangCode("AZ");
    initialSpecsValues.current = {
      name: record.name,
      group_name_id: record.group_name_id,
      category_id: record.category_id,
      show_in_filter: record.show_in_filter,
      price_mode: record.price_mode,
      options: record.options.map((opt) => ({
        id: opt.id,
        text: opt.text,
        translations: opt.translations.map((l) => ({
          lang_code: l.lang_code,
          text: l.text,
        })),
      })),
      translations:
        record.translations.map((l) => ({
          lang_code: l.lang_code,
          name: l.name,
        })) || [],
    };
    const optionsWithTranslations =
      record.options.length > 0
        ? record.options.map((option) => ({
            id: option.id || "",
            text: option.text,
            translations: langs.map((lang) => {
              const translation = option.translations.find(
                (t) => t.lang_code === lang.code
              );
              return {
                lang_code: lang.code,
                text: translation ? translation.text : "",
              };
            }),
          }))
        : [
            {
              id: "",
              text: "",
              translations: langs.map((lang) => ({
                lang_code: lang.code,
                text: "",
              })),
            },
          ];

    setData(optionsWithTranslations);
    setFieldValue("options", optionsWithTranslations);
  };

  return (
    <div id="pages">
      <Helmet>
        <title>BSC | Xüsusiyyətlər</title>
      </Helmet>
      {loading ? (
        <Spinner />
      ) : (
        <div>
          <div className="page_head">
            <h3 className="page_title">Kateqoriyaların Xüsusiyyətləri</h3>
          </div>
          <div className="pages_components">
            <MainButton onClick={handleAddClick}>
              <FaPlus size={20} />
            </MainButton>
            <div className="pages_table">
              <Table<SpecsDataType>
                scroll={{ x: "100%" }}
                columns={columns}
                dataSource={specs}
                rowKey={(record) => record.id.toString()}
                pagination={false}
              />
            </div>
          </div>
        </div>
      )}

      <Modal
        title={
          isEditing ? "Xüsusiyyəti redaktə edin" : "Yeni Xüsusiyyət əlavə edin"
        }
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setSelectedName(undefined);
          setSelectedCategoryId(null);
        }}
        footer={null}
        style={{ minWidth: "340px" }}
        centered
      >
        <form className="modal_form" onSubmit={handleSubmit}>
          <ChangeLanguage
            langCode={langCode}
            setLangCode={setLangCode}
            values={values}
            setFieldValue={setFieldValue}
            langs={langs}
          />
          <div className="form_input">
            <label className="label" htmlFor="name">
              Xüsusiyyət adı {langCode}
            </label>
            <div className="input_wrapper">
              <input
                name="name"
                id="name"
                placeholder="Xüsusiyyət adı"
                value={
                  langCode === "AZ"
                    ? values.name
                    : values.translations.find((t) => t.lang_code === langCode)
                        ?.name || ""
                }
                onChange={(e) => handleTranslationChange(e.target.value)}
              />
              <span className="focus_border"></span>
            </div>
            <ErrorMessage error={errors.name} touched={touched.name} />
          </div>
          <div className="form_input">
            <div className="input_wrapper">
              <SelectGroupName
                setFieldValue={setFieldValue}
                group_name_id={editingRecord?.group_name_id ?? null}
                selectedName={selectedName}
                setSelectedName={setSelectedName}
              />
            </div>
          </div>
          {data.map((item, index) => (
            <div key={index} className="form_input options">
              <label className="label" htmlFor="Text">
                Seçimlər {langCode}
              </label>
              <div className="input_wrapper">
                <div className="option-row">
                  <input
                    placeholder={`Seçim ${index > 0 ? index + 1 : ""}`}
                    value={
                      langCode === "AZ"
                        ? item.text
                        : item.translations.find(
                            (t) => t.lang_code === langCode
                          )?.text || ""
                    }
                    onChange={(e) => {
                      handleOptionChange(index, e.target.value, langCode);
                    }}
                  />
                  {index === 0 && (
                    <button
                      type="button"
                      onClick={handleAddOption}
                      className="add_opt"
                    >
                      <i className="fa-solid fa-plus"></i>
                    </button>
                  )}
                  {index > 0 && (
                    <button
                      type="button"
                      className="remove_opt"
                      onClick={() => handleRemoveOption(index)}
                    >
                      <i className="fa-solid fa-minus"></i>
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}

          <div className="form_input">
            <SelectCategory
              setFieldValue={setFieldValue}
              categroryName={editingRecord?.category?.name}
              selectedCategoryId={selectedCategoryId}
              setSelectedCategoryId={setSelectedCategoryId}
            />
            <ErrorMessage
              error={errors.category_id}
              touched={touched.category_id}
            />
          </div>
          <div className="form_input">
            <label className="active_label" htmlFor="">
              Məhsulun qiymətinə təsir edir mi?
            </label>
            <Switch
              checked={values.price_mode}
              onChange={(checked) => setFieldValue("price_mode", checked)}
            />
          </div>
          <div className="form_input">
            <label className="active_label" htmlFor="">
              Filtrdə göstərilməsi
            </label>
            <Switch
              checked={values.show_in_filter}
              onChange={(checked) => setFieldValue("show_in_filter", checked)}
            />
          </div>
          <div>
            <MainButton full type="submit" className="form_add_btn">
              {isEditing ? "Redaktə edin" : "Əlavə edin"}
            </MainButton>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default CategorySpecs;
